import { sectionOfWidget } from "../homeSectionModel.ts"
import type { HomePageSectionId, HomeUnitSpec } from "./homePageLayout"
import {
  CASH_FLOW_SLUG,
  NBA_FEED_SLUG,
  PROJECT_STATUS_SLUG
} from "./widgetsResponseAdapter"

/**
 * My View's placeable widgets: what Edit layout moves, resizes, renames and
 * removes.
 *
 * A widget here is a CARD, not a backend widget. Some cards compose several
 * backend widgets and some read none at all (Approvals & signatures,
 * Locations), so a card has its own id and names the widget(s) it reads
 * separately. Removing a card removes the card, never data another card
 * still reads — removing Upcoming milestones must not blank the "Milestones
 * this week" count.
 *
 * The KPI strip and the favourite-report links are page furniture, not
 * widgets: the strip is the page's operational summary and always leads it,
 * and the links are a footer. Neither has a header to drag by.
 */
export const HOME_UNIT = {
  priorityActions: "home:priority_actions",
  locations: "home:locations",
  revenue: "home:revenue",
  receivables: "home:receivables",
  overdue: "home:overdue",
  cashFlow: "home:cash_flow",
  snapshot: "home:snapshot",
  milestones: "home:milestones",
  approvals: "home:approvals",
  pipeline: "home:pipeline",
  activity: "home:activity"
} as const

export type HomeUnitId = (typeof HOME_UNIT)[keyof typeof HOME_UNIT]

export const isHomeUnitId = (id: string): id is HomeUnitId =>
  (Object.values(HOME_UNIT) as string[]).includes(id)

export const PIPELINE_SLUG = "leads.lead_stages.chart"
export const ACTIVITY_SLUG = "activity_feed..feed"
export const MILESTONES_SLUG = "projects.upcoming_milestones.list"
export const SNAPSHOT_SLUG = "financial.portfolio_financial_snapshot.snapshot"
export const REVENUE_SLUG = "financial.revenue_in_window.kpi"
export const RECEIVABLES_SLUG = "financial.outstanding_receivables.kpi"
export const OVERDUE_SLUG = "financial.overdue_receivables.kpi"

/**
 * The backend widget a single-widget card reads. A legacy layout that hid
 * this slug (before cards had ids of their own) still reads as the card
 * removed.
 */
export const BACKING_SLUG: Partial<Record<HomeUnitId, string>> = {
  [HOME_UNIT.priorityActions]: NBA_FEED_SLUG,
  [HOME_UNIT.revenue]: REVENUE_SLUG,
  [HOME_UNIT.receivables]: RECEIVABLES_SLUG,
  [HOME_UNIT.overdue]: OVERDUE_SLUG,
  [HOME_UNIT.cashFlow]: CASH_FLOW_SLUG,
  [HOME_UNIT.snapshot]: SNAPSHOT_SLUG,
  [HOME_UNIT.milestones]: MILESTONES_SLUG,
  [HOME_UNIT.pipeline]: PIPELINE_SLUG,
  [HOME_UNIT.activity]: ACTIVITY_SLUG
}

/**
 * Widgets that only feed a card or the KPI strip. They are never drawn as
 * cards of their own on My View.
 */
export const CONSUMED_SLUGS: ReadonlySet<string> = new Set([
  "projects.active_projects.kpi",
  "projects.delayed_projects.kpi",
  "financial.pending_approvals.kpi",
  "projects.pending_approvals.kpi",
  "leads.win_rate.kpi",
  PROJECT_STATUS_SLUG,
  ...Object.values(BACKING_SLUG)
])

/**
 * Sizes in grid rows (a row step is 24px: `24h - 16` px tall). Defaults are
 * each card's compact content height, so paired cards open balanced and
 * without blank space; minimums are where the content stops being readable.
 */
export const HOME_UNIT_SPECS: Record<HomeUnitId, HomeUnitSpec> = {
  [HOME_UNIT.priorityActions]: {
    id: HOME_UNIT.priorityActions,
    section: "overview",
    w: 12,
    h: 7,
    minW: 6,
    minH: 4,
    autoHeight: true
  },
  [HOME_UNIT.locations]: {
    id: HOME_UNIT.locations,
    section: "overview",
    w: 12,
    h: 16,
    minW: 8,
    minH: 12
  },
  [HOME_UNIT.revenue]: {
    id: HOME_UNIT.revenue,
    section: "financial",
    w: 4,
    h: 4,
    minW: 3,
    minH: 4
  },
  [HOME_UNIT.receivables]: {
    id: HOME_UNIT.receivables,
    section: "financial",
    w: 4,
    h: 4,
    minW: 3,
    minH: 4
  },
  [HOME_UNIT.overdue]: {
    id: HOME_UNIT.overdue,
    section: "financial",
    w: 4,
    h: 4,
    minW: 3,
    minH: 4
  },
  [HOME_UNIT.cashFlow]: {
    id: HOME_UNIT.cashFlow,
    section: "financial",
    w: 6,
    h: 11,
    minW: 4,
    minH: 9
  },
  [HOME_UNIT.snapshot]: {
    id: HOME_UNIT.snapshot,
    section: "financial",
    w: 6,
    h: 11,
    minW: 5,
    minH: 9
  },
  [HOME_UNIT.milestones]: {
    id: HOME_UNIT.milestones,
    section: "schedule",
    w: 6,
    h: 9,
    minW: 4,
    minH: 6
  },
  [HOME_UNIT.approvals]: {
    id: HOME_UNIT.approvals,
    section: "schedule",
    w: 6,
    h: 9,
    minW: 4,
    minH: 6
  },
  [HOME_UNIT.pipeline]: {
    id: HOME_UNIT.pipeline,
    section: "pipeline",
    w: 6,
    h: 11,
    minW: 4,
    minH: 8
  },
  [HOME_UNIT.activity]: {
    id: HOME_UNIT.activity,
    section: "pipeline",
    w: 6,
    h: 11,
    minW: 4,
    minH: 7
  }
}

/** The order cards open in, which is also their order inside a section. */
export const HOME_UNIT_ORDER: readonly HomeUnitId[] = [
  HOME_UNIT.priorityActions,
  HOME_UNIT.locations,
  HOME_UNIT.revenue,
  HOME_UNIT.receivables,
  HOME_UNIT.overdue,
  HOME_UNIT.cashFlow,
  HOME_UNIT.snapshot,
  HOME_UNIT.milestones,
  HOME_UNIT.approvals,
  HOME_UNIT.pipeline,
  HOME_UNIT.activity
]

export const FINANCIAL_METRIC_UNITS: readonly HomeUnitId[] = [
  HOME_UNIT.revenue,
  HOME_UNIT.receivables,
  HOME_UNIT.overdue
]

const TYPE_SIZE: Record<string, Omit<HomeUnitSpec, "id" | "section">> = {
  kpi: { w: 3, h: 4, minW: 3, minH: 4 },
  progress: { w: 3, h: 4, minW: 3, minH: 4 },
  chart: { w: 6, h: 11, minW: 4, minH: 8 },
  snapshot: { w: 6, h: 11, minW: 5, minH: 9 },
  table: { w: 6, h: 11, minW: 4, minH: 8 },
  list: { w: 6, h: 9, minW: 4, minH: 6 },
  feed: { w: 6, h: 9, minW: 4, minH: 6 },
  alert: { w: 6, h: 9, minW: 4, minH: 6 },
  map: { w: 12, h: 16, minW: 6, minH: 12 }
}

const PAGE_SECTION_OF: Record<string, HomePageSectionId> = {
  today: "overview",
  locations: "overview",
  financial: "financial",
  schedule: "schedule",
  pipeline: "pipeline"
}

/**
 * Any other catalog widget the user adds lands as a card of its own, in the
 * section its slug reads under (`sectionOfWidget`), at its type's size.
 */
export const genericUnitSpec = (slug: string): HomeUnitSpec => {
  const type = slug.split(".").at(-1) ?? ""
  const size = TYPE_SIZE[type] ?? { w: 6, h: 9, minW: 4, minH: 6 }
  const section = PAGE_SECTION_OF[sectionOfWidget(slug, { type })] ?? "overview"
  return { id: slug, section, ...size }
}
